import React, {
  useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef,
} from 'react';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
export const speechSupported = !!SpeechRecognition;

// Reusable "Sprechen" voice-input control (Web Speech API). Dictates into a
// field via setValue and respects manual edits: the consumer calls
// markManualEdit() (exposed through the ref) from the field's onChange so that
// typing always wins over the live transcript. Shared across the main intake
// screen and the per-question custom-answer field (Release-2 Topic 1).
const VoiceInput = forwardRef(function VoiceInput({ value, setValue, className }, ref) {
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const manualEditRef = useRef(false);
  const baseRef = useRef('');
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);

  useEffect(() => {
    if (!SpeechRecognition) return undefined;
    const recognition = new SpeechRecognition();
    recognition.lang = 'de-DE';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      if (manualEditRef.current) return;
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      const base = baseRef.current;
      setValue(base ? base + ' ' + transcript : transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    return () => { if (recognitionRef.current) recognitionRef.current.abort(); };
  }, [setValue]);

  const start = useCallback(() => {
    if (!recognitionRef.current) return;
    manualEditRef.current = false;
    baseRef.current = '';
    setValue('');
    recognitionRef.current.start();
    setIsListening(true);
    setIsPaused(false);
  }, [setValue]);

  const pause = useCallback(() => {
    if (!recognitionRef.current) return;
    recognitionRef.current.stop();
    setIsListening(false);
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    if (!recognitionRef.current) return;
    manualEditRef.current = false;
    baseRef.current = valueRef.current;
    recognitionRef.current.start();
    setIsListening(true);
    setIsPaused(false);
  }, []);

  const stop = useCallback(() => {
    if (!recognitionRef.current) return;
    if (isListening || isPaused) {
      recognitionRef.current.stop();
      setIsListening(false);
      setIsPaused(false);
    }
  }, [isListening, isPaused]);

  useImperativeHandle(ref, () => ({
    markManualEdit: () => { manualEditRef.current = true; },
    stop,
    isActive: () => isListening || isPaused,
  }), [stop, isListening, isPaused]);

  if (!SpeechRecognition) return null;

  return (
    <div className={`voice-input-group${className ? ' ' + className : ''}`}>
      {!isListening && !isPaused && (
        <button className="btn btn-voice" onClick={start} type="button" title="Spracheingabe starten">
          🎤 Sprechen
        </button>
      )}
      {isListening && (
        <button className="btn btn-voice listening" onClick={pause} type="button" title="Aufnahme pausieren">
          ⏸ Pausieren
        </button>
      )}
      {isPaused && (
        <button className="btn btn-voice" onClick={resume} type="button" title="Aufnahme fortsetzen">
          ▶ Fortsetzen
        </button>
      )}
      {!isListening && <span className="voice-hint">oder einfach tippen</span>}
    </div>
  );
});

export default VoiceInput;
